import { useEffect, useState } from 'react'
import L from 'leaflet'
import { MapContainer, TileLayer, useMap } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'

import './BranchMapPicker.css'

// Puerto Princesa city center — used as the default map center when no pin
// is set yet (every V1 tenant is in PH).
const DEFAULT_CENTER: [number, number] = [9.7392, 118.7353]
const DEFAULT_ZOOM = 16

interface Props {
  value: { lat: number; lng: number } | null
  onChange: (next: { lat: number; lng: number }) => void
}

/**
 * Slice 4b iteration — Google-Maps-style "drag the map under a fixed pin"
 * picker on an OSM tile layer (no API key). The pin is a CSS overlay anchored
 * to the visual center of the map; as the owner drags the map, the lat/lng
 * underneath updates on `moveend`. This is more intuitive than the original
 * draggable-Leaflet-marker pattern — owners drag the map, not the pin.
 *
 * Initial state: pin is ALWAYS visible. On first open, it sits at value
 * (existing coords) or DEFAULT_CENTER (Puerto Princesa) and stages a pending
 * onChange immediately so the parent can save without the owner having to
 * tap anything. "Use my current location" jumps the map there.
 *
 * Out of scope: address autocomplete, server-side geocoding. The owner drops
 * the pin manually — see SLICE-4b decisions.
 */
export function BranchMapPicker({ value, onChange }: Props) {
  const initialCenter: [number, number] = value
    ? [value.lat, value.lng]
    : DEFAULT_CENTER
  const [geoBusy, setGeoBusy] = useState(false)
  // Seed the parent with the default center on first mount when no value is
  // set yet — the picker is meant to "have a pin from the start", so the
  // owner can just hit Save if the default location is correct.
  useEffect(() => {
    if (!value) {
      onChange({ lat: DEFAULT_CENTER[0], lng: DEFAULT_CENTER[1] })
    }
    // Mount-only seed; subsequent value/onChange identity changes don't
    // re-seed (which would clobber the owner's in-progress drag).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const useMyLocation = () => {
    if (!navigator.geolocation) return
    setGeoBusy(true)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onChange({ lat: pos.coords.latitude, lng: pos.coords.longitude })
        setGeoBusy(false)
      },
      () => setGeoBusy(false),
      { timeout: 10_000 },
    )
  }

  return (
    <div className="branch-map-picker">
      <div className="branch-map-picker-map-wrap">
        <MapContainer
          center={initialCenter}
          zoom={DEFAULT_ZOOM}
          scrollWheelZoom={false}
          className="branch-map-picker-map"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapMoveTracker value={value} onChange={onChange} />
        </MapContainer>
        {/* Fixed center-pin overlay — sits above the map at visual center,
            doesn't move when the map pans. The map slides underneath it. */}
        <div className="branch-map-picker-center-pin" aria-hidden="true">
          <svg viewBox="0 0 24 32" className="branch-map-picker-pin-svg">
            <path
              d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20c0-6.6-5.4-12-12-12z"
              fill="#C8553D"
              stroke="#1A1A1A"
              strokeWidth="1.5"
            />
            <circle cx="12" cy="12" r="4" fill="#FFFFFF" />
          </svg>
        </div>
      </div>
      <div className="branch-map-picker-actions">
        <button
          type="button"
          className="owner-btn"
          onClick={useMyLocation}
          disabled={geoBusy}
        >
          {geoBusy ? 'Getting location…' : 'Use my current location'}
        </button>
        {value && (
          <span className="branch-map-picker-coords">
            {value.lat.toFixed(5)}, {value.lng.toFixed(5)}
          </span>
        )}
      </div>
      <p className="branch-map-picker-hint">
        Drag the map so the pin sits on your branch.
      </p>
    </div>
  )
}

/**
 * Imperative bridge between the parent's `value` and Leaflet's pan state.
 * Two responsibilities:
 *   1. When the map finishes panning (`moveend`), publish the new center
 *      coordinates upstream so the parent can save them.
 *   2. When the parent's `value` changes externally (e.g. "Use my current
 *      location" or a fresh row), recenter the map so the visible pin
 *      aligns with the new value.
 */
function MapMoveTracker({ value, onChange }: Props) {
  const map = useMap()

  // External value → map center sync. Triggers when value updates from
  // outside the map (geolocation button, parent prop change).
  useEffect(() => {
    if (!value) return
    const current = map.getCenter()
    // Tolerance is a few meters of float jitter — without this we'd
    // ping-pong between map and parent forever.
    if (
      Math.abs(current.lat - value.lat) > 1e-5 ||
      Math.abs(current.lng - value.lng) > 1e-5
    ) {
      map.setView([value.lat, value.lng], Math.max(map.getZoom(), DEFAULT_ZOOM))
    }
  }, [value?.lat, value?.lng, map])

  // Map center → parent value sync. Fires when the user finishes panning.
  useEffect(() => {
    const handler = () => {
      const c = map.getCenter()
      onChange({ lat: c.lat, lng: c.lng })
    }
    map.on('moveend', handler)
    return () => {
      map.off('moveend', handler)
    }
  }, [map, onChange])

  return null
}

// Leaflet's default broken-image-marker quirk is irrelevant here — we don't
// use Leaflet Markers at all (the pin is a static CSS overlay). Saves us the
// asset-import dance from the previous iteration.
void L
